import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    TextField,
    InputAdornment,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Chip,
    Menu,
    MenuItem,
    IconButton,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { format } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { SmartToy as AIIcon, Visibility as VisibilityIcon, Edit as EditIcon } from '@mui/icons-material';

import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { contentService, getTrialStatus } from '../../services/api';
import Loader from '../../components/common/Loader';
import EmptyState from '../../components/common/EmptyState';
import { LibraryAdd } from '@mui/icons-material';
import SkillsUploadDialog from './SkillsUploadDialog';
import AIProcessingModal from './AIProcessingModal';
import { useContentPlayer } from '../../context/ContentPlayerContext';
import StatusPill from '../shared/StatusPill';

const ContentsList = () => {
    const navigate = useNavigate();
    const { openContentPlayer } = useContentPlayer();
    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [contentToDelete, setContentToDelete] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [skillsUploadOpen, setSkillsUploadOpen] = useState(false);

    const [aiProcessingModal, setAiProcessingModal] = useState({
        open: false,
        contentId: null,
        processingType: 'batch'
    });

    const [limits, setLimits] = useState({ activeCount: 0, totalSize: 0, maxCount: 30, maxSize: 2 * 1024 * 1024 * 1024 });
    const [isTrialInstance, setIsTrialInstance] = useState(false);

    const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
    const [rowMenuContentId, setRowMenuContentId] = useState(null);

    const handleRowMenuOpen = (e, id) => {
        setRowMenuAnchor(e.currentTarget);
        setRowMenuContentId(id);
    };
    const handleRowMenuClose = () => {
        setRowMenuAnchor(null);
        setRowMenuContentId(null);
    };
    const formatDate = (dateString) => {
        try {
            return format(new Date(dateString), 'MMM d, yyyy');
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };

    const getStatusLabel = (status) => {
        const statusCode = Number(status);
        if (statusCode === 1) return 'Draft';
        if (statusCode === 2) return 'Retired';
        if (statusCode === 3) return 'Published';
        return 'Unknown';
    };
    // Removed 12 Oct 2026 (K21.2 pts 21, 28): the commented-out copy of the old getBatchStatusMeta, left when the
    // list moved to the shared StatusPill. Its fixes (Summarising, a finished file showing its content status) are in
    // the function below.

    
    const getBatchStatusMeta = (content) => {
        if (content?.batchStatusLabel) {
            const label = content.batchStatusLabel.trim();
            // The stage reads 'Summarizing' everywhere (K2.3 pt 17), so the label is the pill's stage name. Removed 2 Nov
            // 2026 (K2.3 pt 17): mapping the server's 'Summarising' to 'summarizing'; the server works the label out on
            // each request and now sends 'Summarizing'.
            const status = label.toLowerCase();

            return {
            label,
            status,
            };
        }

        const rawMetadata = content?.file_metadata;

        if (!rawMetadata) return null;

        try {
            const metadata =
            typeof rawMetadata === 'string'
                ? JSON.parse(rawMetadata)
                : rawMetadata;

            if (!metadata || metadata.batchIngest !== true) {
            return null;
            }

            const rawStage =
            metadata.currentStage ||
            metadata.stage ||
            (content.ai_processing_status === 'completed'
                ? 'completed'
                : content.ai_processing_status === 'failed'
                ? 'failed'
                : content.ai_processing_status === 'processing'
                    ? 'reading'
                    : 'pending');

            // Normalize stage keys without changing display labels.
            const stage = String(rawStage).trim().toLowerCase();
            // A finished file shows its content status (Published / Draft).
            // Removed 9 Oct 2026 (K21.2 pt 28): the "Ready" label for a finished file.
            if (stage === 'completed' || content.ai_processing_status === 'completed') return null;

            const stageMap = {
            uploaded: {
                label: 'Uploading',
                status: 'uploading',
            },
            reading: {
                label: 'Reading',
                status: 'reading',
            },
            summarizing: {
                label: 'Summarizing',
                status: 'summarizing',
            },
            indexing: {
                label: 'Indexing',
                status: 'indexing',
            },
            suggesting: {
                label: 'Suggesting skills',
                status: 'suggesting skills',
            },
            'suggesting skills': {
                label: 'Suggesting skills',
                status: 'suggesting skills',
            },
            retrying: {
                label: 'Retrying',
                status: 'retrying',
            },
            duplicate: {
                label: 'Duplicate',
                status: 'duplicate',
            },
            limitreached: {
                label: 'Daily limit reached',
                status: 'daily limit reached',
            },
            skipped: {
                label: 'Skipped',
                status: 'skipped',
            },
            completed: {
                label: 'Ready',
                status: 'ready',
            },
            failed: {
                label: 'Failed',
                status: 'failed',
            },
            pending: {
                label: 'Queued',
                status: 'queued',
            },
            };

            return (
            stageMap[stage] || {
                label: 'Processing',
                status: 'processing',
            }
            );
        } catch (error) {
            return null;
        }
    };    


    const fetchContents = async () => {
        setLoading(true);
        try {
            const response = await contentService.getContentsList({
                draw: 1,
                start: page * rowsPerPage,
                length: rowsPerPage,
                search: { value: searchValue }
            });

            // Parse the HTML action buttons from the response
            const contentData = response.data.aaData.map((row, index) => {
                return {
                    name: row[0],
                    updatedAt: formatDate(row[1]),
                    duration: row[2],
                    status: row[3],
                    skills: row[4] || [],
                    actions: row[5],
                    id: row[6],
                    batchStatusLabel: row[7] || null,
                    file_metadata: row[8] || null,
                };
            });

            setContents(contentData);
            setTotalCount(response.data.recordsFiltered);
        } catch (error) {
            console.error('Error fetching content:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContents();
    }, [page, rowsPerPage, searchValue]);

    useEffect(() => {
        // fetch instance trial status on mount
        let mounted = true;
        (async () => {
            try {
                const res = await getTrialStatus();
                if (!mounted) return;
                const instanceType = res.data?.instanceType;
                setIsTrialInstance(instanceType === 'FREE_TRIAL');
            } catch (e) {
                // default to non-trial if API fails
                if (!mounted) return;
                setIsTrialInstance(false);
            }
        })();

        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        // Only fetch limits when running on a trial instance
        if (!isTrialInstance) {
            // Reset limits to safe defaults when not trial
            setLimits({ activeCount: 0, totalSize: 0, maxCount: 30, maxSize: 2 * 1024 * 1024 * 1024 });
            return;
        }

        let mounted = true;
        async function fetchLimits() {
            try {
                const res = await contentService.getLimits();
                if (!mounted) return;
                setLimits(res.data);
            } catch (e) {
                // handle error
            }
        }
        fetchLimits();

        return () => { mounted = false; };
    }, [isTrialInstance]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchValue(value);

        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeoutId = setTimeout(() => {
            setPage(0);
            //fetchContents();
        }, 500);

        setSearchTimeout(timeoutId);
    };

    const handleAddContent = () => {
        navigate('/contents/add');
    };

    const handleEditContent = (id) => {
        navigate(`/contents/form/${id}`);
    };

    const openDeleteDialog = (contentId) => {
        setContentToDelete(contentId);
        setDeleteDialogOpen(true);
    };
    // Close delete confirmation dialog
    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setContentToDelete(null);
    };

    // Handle content deletion after confirmation
    const confirmDelete = async () => {
        try {
            console.log('contentToDelete', contentToDelete);
            await contentService.deleteContent(contentToDelete);
            fetchContents();
            closeDeleteDialog();
        } catch (error) {
            console.error('Error deleting content:', error);
            closeDeleteDialog();
        }
    };

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDownloadCsv = async () => {
        setDownloadLoading(true);
        try {
            const response = await contentService.exportContentsCsv();

            // Create blob and download
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
            link.download = `contents_list_${timestamp}.csv`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            handleMenuClose();
        } catch (error) {
            console.error('Error downloading CSV:', error);
        } finally {
            setDownloadLoading(false);
        }
    };

    // Handle skills upload dialog
    const handleOpenSkillsUpload = () => {
        setSkillsUploadOpen(true);
        handleMenuClose();
    };

    const handleCloseSkillsUpload = () => {
        setSkillsUploadOpen(false);
    };

    const handleSkillsUploadSuccess = () => {
        fetchContents(); // Refresh the content list
    };

    const handleAIProcessing = (contentId = null, processingType = 'batch') => {
        setAiProcessingModal({
            open: true,
            contentId,
            processingType
        });
    };

    const closeAIProcessingModal = () => {
        setAiProcessingModal({
            open: false,
            contentId: null,
            processingType: 'batch'
        });
        // Refresh the content list after processing
        fetchContents();
    };
    const canAddContent = isTrialInstance ? (limits.activeCount < limits.maxCount && limits.totalSize < limits.maxSize) : true;
    return (    
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" gutterBottom className='page-title'>
                    Content Management
                </Typography>                
            </Box>

            <Box sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <TextField
                    placeholder={`Name or Skills`}
                    size="small"
                    value={searchValue}
                    className="pink-filter"                    
                    onChange={handleSearch}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="end">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />

                <Box sx={{ display: 'flex', gap: 1 }}>                    
                    <Button
                        variant="outlined"
                        startIcon={<AIIcon />}
                        onClick={() => handleAIProcessing()}
                        
                    >
                        AI Skill Processing
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleMenuClick}
                        endIcon={<MoreVertIcon />}
                    >
                        More
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                    >
                        <MenuItem onClick={handleDownloadCsv} disabled={downloadLoading}>
                            <DownloadIcon sx={{ mr: 1 }} />
                            {downloadLoading ? 'Downloading...' : 'Download Content List'}
                        </MenuItem>
                        <MenuItem onClick={handleOpenSkillsUpload}>
                            <UploadIcon sx={{ mr: 1 }} />
                            Update Skills
                        </MenuItem>
                    </Menu>
                    {canAddContent && (
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ px: 3, py: 1 }}
                            startIcon={<AddIcon />}
                            onClick={handleAddContent}
                        >
                            Add
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
                <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                    <Table
                        stickyHeader
                        aria-label="sticky table"
                        sx={{
                            width: '100%',
                            tableLayout: 'fixed'
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '32%', minWidth: 220 }}>Name</TableCell>
                                <TableCell sx={{ width: '18%', minWidth: 150 }}>Last Updated</TableCell>
                                <TableCell sx={{ width: '12%', minWidth: 100 }}>Duration</TableCell>
                                <TableCell sx={{ width: '12%', minWidth: 110 }}>Status</TableCell>
                                <TableCell sx={{ width: '20%', minWidth: 180 }}>Skills</TableCell>
                                <TableCell sx={{ width: '6%', minWidth: 72, textAlign: 'center' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        
                        <TableBody>
                        {loading ? (
                            <TableRow>
                            <TableCell colSpan={6} align="center">
                                <Loader />
                            </TableCell>
                            </TableRow>
                        ) : contents.length === 0 ? (
                            <TableRow>
                            <TableCell colSpan={6} align="center">
                                {/* An empty library shows the next step (K21.2 pt 42). Removed 9 Oct 2026: the "No content found" text. */}
                                {searchValue ? (
                                    <EmptyState framed={false} title="No content matches your search" message="Try another name or skill." />
                                ) : (
                                    <EmptyState
                                        framed={false}
                                        icon={LibraryAdd}
                                        title="Your library is empty"
                                        message="Add PDFs, Word files, videos or SCORM packages. Beetsol reads each one and makes it answerable in about a minute."
                                        actionLabel="Add content"
                                        onAction={() => navigate('/contents/add')}
                                    />
                                )}
                            </TableCell>
                            </TableRow>
                        ) : (
                            contents.map((content) => {
                            const batchStatus = getBatchStatusMeta(content);

                            return (
                                <TableRow hover key={content.id}>
                                <TableCell
                                    sx={{
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                    }}
                                >
                                    <Box
                                    sx={{
                                        cursor: 'pointer',
                                        color: 'text.primary',
                                        lineHeight: 1.4,
                                    }}
                                    onClick={() => handleEditContent(content.id)}
                                    >
                                    {content.name}
                                    </Box>
                                </TableCell>

                                <TableCell>{content.updatedAt}</TableCell>

                                <TableCell>{content.duration}</TableCell>

                                {/* Status column */}
                                <TableCell>
                                    {batchStatus ? (
                                    <StatusPill
                                        status={batchStatus.status}
                                        label={batchStatus.label}
                                    />
                                    ) : (
                                    // The content status as its word (Published, Draft, Retired), so the pill reads
                                    // it right; a finished file lands here too (K21.2 pt 28; N10 pt 4).
                                    // Removed 12 Oct 2026 (N10 pt 4): passing the number, which the pill reads as a
                                    // user status (3 = Pending, 1 = Active).
                                    <StatusPill
                                        status={getStatusLabel(content.status).toLowerCase()}
                                        label={getStatusLabel(content.status)}
                                    />
                                    )}
                                </TableCell>

                                {/* Skills column */}
                                <TableCell sx={{ overflowWrap: 'anywhere' }}>
                                    <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                    }}
                                    >
                                    {Array.isArray(content.skills) &&
                                        content.skills.map((skill, idx) => (
                                        <Chip
                                            key={idx}
                                            label={skill.name || skill}
                                            size="small"
                                            variant="outlined"
                                            sx={{ maxWidth: '100%' }}
                                        />
                                        ))}
                                    </Box>
                                </TableCell>

                                {/* Actions column */}
                                <TableCell>
                                    <IconButton
                                    size="small"
                                    onClick={(e) =>
                                        handleRowMenuOpen(e, content.id)
                                    }
                                    sx={{ color: 'text.primary' }}
                                    >
                                    <MoreVertIcon fontSize="small" />
                                    </IconButton>

                                    <Menu
                                    anchorEl={rowMenuAnchor}
                                    open={rowMenuContentId === content.id}
                                    onClose={handleRowMenuClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    slotProps={{
                                        paper: {
                                        sx: {
                                            '& .MuiListItemText-primary': {
                                            color: 'text.primary',
                                            },
                                            '& .MuiListItemIcon-root': {
                                            color: 'primary.main',
                                            minWidth: 'auto !important',
                                            },
                                        },
                                        },
                                    }}
                                    >
                                    <MenuItem
                                        onClick={() => {
                                        openContentPlayer(
                                            null,
                                            content.id,
                                            '/contents'
                                        );
                                        handleRowMenuClose();
                                        }}
                                    >
                                        <ListItemIcon>
                                        <VisibilityIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>View</ListItemText>
                                    </MenuItem>

                                    <MenuItem
                                        onClick={() => {
                                        handleEditContent(content.id);
                                        handleRowMenuClose();
                                        }}
                                    >
                                        <ListItemIcon>
                                        <EditIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>Edit</ListItemText>
                                    </MenuItem>

                                    <MenuItem
                                        onClick={() => {
                                        openDeleteDialog(content.id);
                                        handleRowMenuClose();
                                        }}
                                    >
                                        <ListItemIcon>
                                        <DeleteIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>Delete</ListItemText>
                                    </MenuItem>
                                    </Menu>
                                </TableCell>
                                </TableRow>
                            );
                            })
                        )}
                        </TableBody>

                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={closeDeleteDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this content? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Skills Upload Dialog */}
            <SkillsUploadDialog
                open={skillsUploadOpen}
                onClose={handleCloseSkillsUpload}
                onSuccess={handleSkillsUploadSuccess}
            />

            <AIProcessingModal
                open={aiProcessingModal.open}
                onClose={closeAIProcessingModal}
                contentId={aiProcessingModal.contentId}
                processingType={aiProcessingModal.processingType}
            />
        </Box>
    );
};

export default ContentsList;